// FILE: NewThreadDialogPlaneIssueField.tsx
// Purpose: The "Issue de Plane" combobox inside NewThreadDialog — lists a
//          project's board issues, filtered by text, closed hidden by default.
// Layer: Web components
//
// Split out of NewThreadDialog.tsx because it owns its own async fetch and
// filter lifecycle (per-project, lazy on open) — folding it into the dialog's
// per-open reset would tangle two different reset triggers.
//
// The list is fetched fresh every time it opens and never cached: a board
// changes under you, and a stale picker would offer an issue someone already
// closed. Closed issues are hidden by default rather than absent, since
// reopening work is not rare — the toggle is one click away.

import { useEffect, useMemo, useState } from "react";

import type { ProjectId } from "@taskin-code/contracts";

import { LoaderIcon } from "~/lib/icons";

import { PlaneClient, type PlaneIssueSummary } from "../lib/planeClient";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxTrigger,
} from "./ui/combobox";
import { Button } from "./ui/button";

const CLOSED_GROUPS = new Set(["completed", "cancelled"]);
const NO_ISSUE = "__no_issue__";

export interface NewThreadDialogPlaneIssueFieldProps {
  projectId: ProjectId;
  value: PlaneIssueSummary | null;
  disabled?: boolean;
  onChange: (issue: PlaneIssueSummary | null) => void;
}

export function NewThreadDialogPlaneIssueField({
  projectId,
  value,
  disabled,
  onChange,
}: NewThreadDialogPlaneIssueFieldProps) {
  const [open, setOpen] = useState(false);
  const [issues, setIssues] = useState<ReadonlyArray<PlaneIssueSummary> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showClosed, setShowClosed] = useState(false);

  // A project switch invalidates whatever the previous project's board handed
  // back — otherwise the field would briefly offer another project's issues.
  useEffect(() => {
    setIssues(null);
    setError(null);
    setQuery("");
    setShowClosed(false);
  }, [projectId]);

  useEffect(() => {
    if (!open || issues !== null) return;
    let cancelled = false;
    void PlaneClient.listProjectIssues(projectId)
      .then((result) => {
        if (!cancelled) setIssues(result.issues);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(
          cause instanceof Error ? cause.message : "No se pudieron leer los issues de Plane.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, issues, projectId]);

  const visibleIssues = useMemo(() => {
    if (issues === null) return [];
    const needle = query.trim().toLowerCase();
    return issues
      .filter((issue) => showClosed || !CLOSED_GROUPS.has(issue.stateGroup))
      .filter(
        (issue) =>
          needle.length === 0 ||
          issue.ref.toLowerCase().includes(needle) ||
          issue.title.toLowerCase().includes(needle),
      );
  }, [issues, query, showClosed]);

  const issueById = useMemo(
    () => new Map((issues ?? []).map((issue) => [issue.id, issue] as const)),
    [issues],
  );
  const allItemValues = useMemo(
    () => [NO_ISSUE, ...(issues ?? []).map((issue) => issue.id)],
    [issues],
  );
  const visibleItemValues = useMemo(
    () => [NO_ISSUE, ...visibleIssues.map((issue) => issue.id)],
    [visibleIssues],
  );

  const triggerLabel = value ? `${value.ref} — ${value.title}` : "Sin vincular";

  return (
    <Combobox
      items={allItemValues}
      filteredItems={visibleItemValues}
      value={value?.id ?? NO_ISSUE}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery("");
      }}
    >
      <ComboboxTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start font-normal"
            disabled={disabled}
          />
        }
      >
        <span className="truncate">{triggerLabel}</span>
      </ComboboxTrigger>
      <ComboboxPopup align="start" className="w-[--anchor-width]">
        <div className="flex items-center gap-2 border-b p-1">
          <ComboboxInput
            className="flex-1 rounded-md border-border/60 bg-background shadow-none before:hidden has-focus-visible:border-neutral-500/15 has-focus-visible:ring-0"
            inputClassName="ring-0"
            placeholder="Buscar por referencia o título…"
            showTrigger={false}
            size="sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button
            type="button"
            variant={showClosed ? "secondary" : "ghost"}
            size="xs"
            className="shrink-0"
            onClick={() => setShowClosed((previous) => !previous)}
          >
            {showClosed ? "Ocultar cerrados" : "Ver cerrados"}
          </Button>
        </div>

        {error !== null ? (
          <p className="px-3 py-2 text-xs text-destructive">{error}</p>
        ) : issues === null ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <LoaderIcon className="size-3.5 animate-spin" />
            Cargando issues…
          </div>
        ) : (
          <>
            <ComboboxEmpty>Ningún issue coincide con la búsqueda.</ComboboxEmpty>
            <ComboboxList className="max-h-64">
              <ComboboxItem
                hideIndicator
                index={0}
                value={NO_ISSUE}
                onClick={() => onChange(null)}
                className="text-muted-foreground"
              >
                Sin vincular
              </ComboboxItem>
              {visibleIssues.map((issue, index) => (
                <ComboboxItem
                  hideIndicator
                  key={issue.id}
                  index={index + 1}
                  value={issue.id}
                  onClick={() => {
                    const picked = issueById.get(issue.id);
                    if (picked) onChange(picked);
                  }}
                >
                  <div className="flex w-full flex-col items-start gap-0.5 py-0.5">
                    <span className="flex w-full items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {issue.ref}
                      </span>
                      {issue.stateName.length > 0 ? (
                        <span className="rounded-full bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground">
                          {issue.stateName}
                        </span>
                      ) : null}
                    </span>
                    <span className="w-full truncate text-sm">{issue.title}</span>
                  </div>
                </ComboboxItem>
              ))}
            </ComboboxList>
          </>
        )}
      </ComboboxPopup>
    </Combobox>
  );
}
